import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function SchoolBusCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      reg_no: "",
      driver: "",
      driver_phone: "",
      helper: "",
      helper_phone: "",
      route: "",
      bus_type: "school_bus",
      company: "",
      contractor_phone: "",
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      reg_no: Yup.string().required("Registration No. is required"),
      sort_order: Yup.string().required("Sort Order is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        reg_no: values.reg_no,
        driver: values.driver,
        driver_phone: values.driver_phone,
        helper: values.helper,
        helper_phone: values.helper_phone,
        route: values.route,
        bus_type: values.bus_type,
        company: values.company,
        contractor_phone: values.contractor_phone,
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("school-buses/" + id, payload);
        } else {
          response = await request.post("school-buses/create", payload);
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Bus updated successfully!" : "Bus created successfully!");
          navigate("/admin/school-buses");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`school-buses/${id}`);
          const item = response.data.data;
          formik.setValues({
            reg_no: item.reg_no || "",
            driver: item.driver || "",
            driver_phone: item.driver_phone || "",
            helper: item.helper || "",
            helper_phone: item.helper_phone || "",
            route: item.route || "",
            bus_type: item.bus_type || "school_bus",
            company: item.company || "",
            contractor_phone: item.contractor_phone || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Bus" : "Create New Bus"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/school-buses"),
              }}
              primaryButton={{
                label: isEdit ? "Update Bus" : "Create Bus",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="reg_no" label="Reg. No." type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="bus_type"
                    label="Bus Type"
                    formik={formik}
                    options={[
                      { value: "school_bus", label: "School Bus" },
                      { value: "contract_bus", label: "Contract Bus" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="driver" label="Driver" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="driver_phone" label="Driver Phone" type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="helper" label="Helper" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="helper_phone" label="Helper Phone" type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="route" label="Route" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--3" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="company" label="Company" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="contractor_phone" label="Contractor Phone" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}